import useOrganizationInfoDescriptionFunctions from 'components/Dashboard/hooks/useOrganizationInfoDescriptionFunctions';
import createNotification from 'components/Settings/Utilities/CreateNotification';
import { useEffect, useRef } from 'react';

const GenerateDescriptionComponent = (props) => {
  const {
    clientDescriptionState,
    teamName,
    loadingState,
    errorFunction,
    errorElement,
    subtextClass,
    notAutoGenerate,
    boxClass = '',
    clientWebsite,
  } = props ?? {};
  const { clientDescription, setClientDescription } = clientDescriptionState ?? {};
  const { isLoading, setIsLoading } = loadingState ?? {};

  const { generateOrganisationDescription } = useOrganizationInfoDescriptionFunctions();

  const clientArea = useRef(null);
  const typingTimeOut = useRef(null);
  const initialCall = useRef(null);
  initialCall.current = async (source) => {
    if (!notAutoGenerate) {
      try {
        setIsLoading(true);
        const results = await generateOrganisationDescription({ source, teamName, clientWebsite });
        if (!!results) {
          setClientDescription(results);
          setIsLoading(false);
        }
      } catch (error) {}
    }
  };

  useEffect(() => {
    const source = new AbortController();
    initialCall.current(source);
    return () => {
      source.abort('Generation of description cancelled by the user');
      setIsLoading(false);
    };
  }, []);

  const adjustTextareaHeight = (element) => {
    if (element) {
      const maxHeight = 320;
      element.style.height = 'auto';
      element.style.height = `${element.scrollHeight <= maxHeight ? element.scrollHeight : maxHeight}px`;
      element.style.overflow = element.scrollHeight > maxHeight ? 'auto' : 'hidden';
    }
  };

  useEffect(() => {
    if (clientArea?.current && !isLoading) {
      adjustTextareaHeight(clientArea.current);
      const focusAtBeginning = () => {
        const textarea = clientArea.current;
        if (textarea) {
          textarea.focus();
          const value = textarea?.value;
          textarea.setSelectionRange(value?.length, value?.length);
        } else {
          requestAnimationFrame(focusAtBeginning);
        }
      };
      setTimeout(focusAtBeginning, 300);
    }
  }, [isLoading]);

  return (
    <>
      {isLoading ? (
        <div className={`rounded border p-5 mt-4 text-center ${boxClass}`}>
          <span className='title-h5-m-bold d-block mx-auto my-3' style={{ maxWidth: '250px' }}>
            We're generating a description for you to review
          </span>
          <span className={`${subtextClass ?? 'main-light-text'} paragraph-p2 user-select-none mb-4 d-block`}>
            This typically takes a few seconds
          </span>
          <div className='waiting-bar mx-auto mb-5'>
            <div className='waiting-bar-indicator' />
          </div>
        </div>
      ) : (
        <div className='form-group'>
          <textarea
            ref={clientArea}
            data-hj-allow
            value={clientDescription}
            type='text'
            className='input-empty notes-textarea w-100 border border-grey rounded-sm p-3 main-input'
            style={{ resize: 'none', overflow: 'hidden' }}
            onChange={(e) => {
              setClientDescription(e.target.value);
              adjustTextareaHeight(e.target);
            }}
            onKeyDown={(e) => {
              if (typingTimeOut.current) clearTimeout(typingTimeOut.current);
              typingTimeOut.current = setTimeout(() => {
                if (e.target?.value?.trim()?.length < 100) {
                  if (errorFunction) {
                    errorFunction();
                  } else {
                    createNotification('danger', 'Please enter a description that is at least 100 characters');
                  }
                }
              }, 500);
            }}
            placeholder={'Write client description...'}
          />
          {errorElement && <>{errorElement()}</>}
        </div>
      )}
    </>
  );
};

export default GenerateDescriptionComponent;
